/* Canonical global mask sizing. Build copies this exact file into the account service. */
(function (scope) {
  'use strict';
  // Records keep 'size-rules/1.0.0' after the 10 Oct 2026 boundary change below. A record holds only the measured cm;
  // every page, the cart and the account service work the size out from that number when they use it, so records saved
  // before the change get the new size without being rewritten. A new version string would make valid() here, clean()
  // in face-fit-memory.js and the account service (all exact matches) drop every saved and remembered measurement.
  const version = 'size-rules/1.0.0';
  const engine = 'face-fit-engine/1.0.0';
  // The width as every page prints it: cm to the nearest tenth, halves up, the way a shopper rounds it (13.95 cm reads
  // 14.0 cm; toFixed(1) would print 13.9 because 13.95 is stored as 13.9499…, so pages print display(cm).cm, never
  // cm.toFixed(1)). Sizes are read from this printed value, never from the unrounded one, so the number a shopper sees
  // and the size next to it always sit in the same row of the size chart. Exact for widths typed with up to 4 decimals.
  function shown(cm) { return Math.round(cm * 10) / 10; }
  // Size chart: S 12–13 cm, M 13–14, L 14–15, XL 15–16, XXL 16–17. A width on a boundary gets the upper size (owner,
  // 10 Oct 2026): 13.95–13.99 cm print "14.0 cm" and are L; 5.5 in (13.97 cm) is L too.
  function sizeFor(cm) {
    if (typeof cm !== 'number' || !Number.isFinite(cm)) return null;
    const value = shown(cm);
    if (value < 12 || value > 17) return null;
    return value < 13 ? 'S' : value < 14 ? 'M' : value < 15 ? 'L' : value < 16 ? 'XL' : 'XXL';
  }
  // Both printed numbers and the size. Inches come from the printed cm, so they also fall in the chart's inch row for
  // that size (S 4.7–5.1 in, M 5.1–5.5, L 5.5–5.9, XL 5.9–6.3, XXL 6.3–6.7): 14.0 cm is 5.51 in and L, never 5.49 in.
  function display(cm) {
    const value = shown(cm);
    return {cm: value.toFixed(1), inches: (value / 2.54).toFixed(2), size: sizeFor(cm)};
  }
  function valid(record) {
    return !!record && record.schema === 1 && record.rules === version && record.engine === engine &&
      record.method === 'camera' && !!sizeFor(record.cm) && Number.isFinite(Date.parse(record.measuredAt));
  }
  function text(record) { const value = display(record.cm); return `${value.cm} cm / ${value.inches} in - ${value.size}`; }
  // Cart lines added before 10 Oct 2026 printed inches from the unrounded cm ("14.4 cm / 5.68 in - L" for 14.43 cm).
  // The same measurement with the same size is still the same text; a line whose size changed is not.
  function sameText(value, record) {
    if (value === text(record)) return true;
    const cm = record.cm;
    return value === `${cm.toFixed(1)} cm / ${(cm / 2.54).toFixed(2)} in - ${sizeFor(cm)}`;
  }
  // Optional diagnostics belong to the completed result, not today's device/session.
  // Missing or invalid legacy diagnostics must never block sizing or checkout.
  function normalizeScanStats(value, completed = true) {
    if (!value || value.schema !== 1 || !Number.isSafeInteger(value.attempts) || value.attempts < 1 ||
      !Number.isSafeInteger(value.retries) || value.retries !== value.attempts - 1 ||
      !Number.isSafeInteger(value.failed) || value.failed < 0 || value.failed > value.attempts || (completed && value.failed === value.attempts)) return null;
    return {schema:1, attempts:value.attempts, retries:value.retries, failed:value.failed};
  }
  const scanStatsKeys = Object.freeze(['_Face Fit Attempts', '_Face Fit Retries', '_Face Fit Failed Attempts']);
  function scanStatsProperties(value, completed = true) {
    const stats = normalizeScanStats(value, completed);
    return stats ? Object.fromEntries(scanStatsKeys.map((key, i) => [key, String([stats.attempts, stats.retries, stats.failed][i])])) : {};
  }
  function stable(values) {
    if (values.length < 60 || values.some(v => !Number.isFinite(v))) return null;
    const sorted = [...values].sort((a,b) => a-b);
    const middle = Math.floor(sorted.length / 2);
    const cm = sorted.length % 2 ? sorted[middle] : (sorted[middle-1] + sorted[middle]) / 2;
    // Reject visibly unstable captures. This checks repeatability, not physical accuracy.
    // The camera keeps the range it accepted before the 10 Oct 2026 size-table change: raw 12–17 cm, unrounded (owner:
    // the measurement engine stays as it was; only the cm → size table changed).
    if (sorted[Math.floor((sorted.length-1)*.9)] - sorted[Math.floor((sorted.length-1)*.1)] > .6 || cm < 12 || cm > 17) return null;
    return cm;
  }
  scope.SpaceArmoryFitRules = Object.freeze({version, engine, sizeFor, display, valid, text, sameText, stable, normalizeScanStats, scanStatsKeys, scanStatsProperties});
})(globalThis);
